// src/components/CoinDetailModal.jsx
import React, { useEffect } from 'react';
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis } from 'recharts'; 
import { Helmet } from 'react-helmet-async'; // SEO Power-up
import { icons } from '../assets/icons';
import { formatCurrency } from '../utils/formatters';
import { useApi } from '../hooks/useApi';

const toSystemTime = (dateString) => {
  if (!dateString) return null;
  const utcString = dateString.includes('T') ? dateString : dateString.replace(' ', 'T') + 'Z';
  return new Date(utcString).toLocaleString(undefined, { 
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const CoinDetailModal = ({ coinId, onClose }) => {
  const { data: coinData, loading: coinLoading, error: coinError, errorStatus: coinErrorStatus, fetchData: fetchCoinDetails } = useApi(null, true);
  const { data: chartData, loading: chartLoading, error: chartError, fetchData: fetchChartDetails } = useApi(null, true);
  const canonical = coinId ? `https://cryptobag.net/coin/${coinId}` : 'https://cryptobag.net/prices';
  const coinNotFound = coinErrorStatus === 404 && !coinData;

  useEffect(() => {
    if (!coinId) return;
    fetchCoinDetails(`/api/coins/${coinId}`);
    fetchChartDetails(`/api/coins/${coinId}/market_chart?vs_currency=usd&days=30`);
  }, [coinId, fetchCoinDetails, fetchChartDetails]);

  if (!coinId) return null;

  const renderChart = () => {
    if (chartError && (!chartData || !chartData.prices?.length)) return <div className="h-64 flex items-center justify-center text-gray-300">Unable to load chart data.</div>;
    if (chartLoading && !chartData) return <div className="h-64 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-400" /></div>;

    const prices = chartData?.prices || [];
    const formattedData = prices.map(([timestamp, price]) => ({
      date: new Date(timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      price,
    }));

    return (
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={formattedData} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
            <defs><linearGradient id="cP" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/><stop offset="95%" stopColor="#6366f1" stopOpacity={0}/></linearGradient></defs>
            <XAxis dataKey="date" hide />
            <Tooltip contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', color: '#fff' }} formatter={(v) => [formatCurrency(v), 'Price']} />
            <Area type="monotone" dataKey="price" stroke="#6366f1" fill="url(#cP)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderContent = () => {
    if (coinLoading && !coinData) return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-400" /></div>;
    if (coinNotFound) {
      return (
        <div className="p-10 text-center space-y-4">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-indigo-400">404</p>
          <h2 className="text-3xl font-black text-white tracking-tighter">Coin Not Found</h2>
          <p className="text-gray-400 max-w-lg mx-auto">
            The requested cryptocurrency could not be found. Browse the live market table to open a supported coin page.
          </p>
          <button
            onClick={onClose}
            className="inline-flex items-center px-4 py-2 rounded-lg bg-indigo-500 text-white font-bold hover:bg-indigo-400 transition-colors"
          >
            Back To Prices
          </button>
        </div>
      );
    }
    if (coinError && !coinData) return <div className="p-6 text-center text-red-400">Error Loading Data</div>;
    if (!coinData) return null;

    const coinCachedAt = toSystemTime(coinData._cachedAt);
    const m = coinData.market_data || {};
    const priceChangePct = m.price_change_percentage_24h ?? null;

    return (
      <div className="p-4 sm:p-6 space-y-6">
        {/* SEO METADATA - This tells Google what this page is about */}
        <Helmet>
          <title>{`${coinData.name} (${coinData.symbol?.toUpperCase()}) Price & Intelligence | CryptoBag`}</title>
          <meta name="description" content={`Real-time ${coinData.name} market data, 30-day price charts, and sentiment analysis. View current price: ${formatCurrency(m.current_price?.usd)}.`} />
          <link rel="canonical" href={canonical} />
        </Helmet>

        <div className="flex items-center space-x-3">
          {coinData.image?.large && <img src={coinData.image.large} alt="" className="w-12 h-12 rounded-full" />}
          <div>
            <h2 className="text-2xl sm:text-3xl font-black text-white flex items-center tracking-tighter">
              {coinData.name} <span className="text-sm text-gray-400 uppercase ml-2">({coinData.symbol})</span>
            </h2>
            <div className="flex items-center gap-3 mt-2">
              {coinData.market_cap_rank && (
                <span className="text-sm font-bold text-gray-400 bg-gray-800 px-3 py-1 rounded">Rank #{coinData.market_cap_rank}</span>
              )}
              {coinCachedAt && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-indigo-500/10 text-gray-400 border border-indigo-500/20">
                  <span className="w-2 h-2 rounded-full bg-indigo-500 mr-2 animate-pulse"></span>
                  Synced: {coinCachedAt}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-baseline gap-3">
            <div className="text-3xl sm:text-4xl font-black text-white tracking-tighter">{formatCurrency(m.current_price?.usd, 'auto')}</div>
            {priceChangePct != null && (
              <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${priceChangePct >= 0 ? 'text-green-400' : 'text-red-400'} bg-white/5`}>
                {priceChangePct >= 0 ? icons.arrowUpRight({ className: 'w-4 h-4 mr-1' }) : icons.arrowDownRight({ className: 'w-4 h-4 mr-1' })}
                {priceChangePct.toFixed(2)}%
              </div>
            )}
          </div>
        </div>

        <div className="bg-gray-900/60 rounded-lg border border-gray-700 pt-1 pl-3 pb-4 pr-4 shadow-inner">
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">30D Price History</span>
          {renderChart()}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div className="bg-gray-900/60 rounded-lg border border-gray-700 p-4 space-y-2">
            <div className="flex justify-between"><span className="text-gray-400">Market Cap</span><span className="text-white font-bold">{formatCurrency(m.market_cap?.usd, 'auto')}</span></div>
            <div className="flex justify-between"><span className="text-gray-400">24h Volume</span><span className="text-white font-bold">{formatCurrency(m.total_volume?.usd, 'auto')}</span></div>
          </div>
          <div className="bg-gray-900/60 rounded-lg border border-gray-700 p-4 space-y-2">
            <div className="flex justify-between"><span className="text-gray-400">Circulating Supply</span><span className="text-white font-bold">{m.circulating_supply?.toLocaleString()}</span></div>
            <div className="flex justify-between"><span className="text-gray-400">Total Supply</span><span className="text-white font-bold">{m.total_supply > 0 ? m.total_supply.toLocaleString() : 'Unlimited'}</span></div>
          </div>
          <div className="bg-gray-900/60 rounded-lg border border-gray-700 p-4 space-y-2">
            <div className="flex justify-between"><span className="text-gray-400">24h High</span><span className="text-white text-green-400 font-bold">{formatCurrency(m.high_24h?.usd, 'auto')}</span></div>
            <div className="flex justify-between"><span className="text-gray-400">24h Low</span><span className="text-white text-red-400 font-bold">{formatCurrency(m.low_24h?.usd, 'auto')}</span></div>
          </div>
          {coinData?.links?.homepage?.[0] && (
            <div className="bg-gray-900/60 rounded-lg border border-gray-700 p-4 flex flex-col justify-center">
              <div className="text-gray-400 mb-1">Official Website</div>
              <a href={coinData.links.homepage[0]} target="_blank" rel="noreferrer" className="text-indigo-400 hover:text-indigo-300 truncate font-bold">{coinData.links.homepage[0]}</a>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose}>
      <Helmet>
        {coinNotFound ? (
          <>
            <title>Coin Not Found | CryptoBag</title>
            <meta name="description" content="The requested cryptocurrency could not be found." />
            <meta name="robots" content="noindex, follow" />
            <link rel="canonical" href="https://cryptobag.net/prices" />
          </>
        ) : coinError && !coinData ? (
          <>
            <title>Coin Data Unavailable | CryptoBag</title>
            <meta name="robots" content="noindex, follow" />
            <link rel="canonical" href={canonical} />
          </>
        ) : coinLoading && !coinData ? (
          <>
            <title>Loading Coin Data | CryptoBag</title>
            <meta name="robots" content="noindex, follow" />
            <link rel="canonical" href={canonical} />
          </>
        ) : null}
      </Helmet>
      <div className="bg-gray-900 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col border border-gray-700" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center p-4 border-b border-gray-700">
          <h2 className="text-lg font-black text-white uppercase italic tracking-tighter">
            Asset <span className="text-indigo-500">Details</span>
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-2">{icons.close({ className: 'w-6 h-6' })}</button>
        </div>
        <div className="flex-grow overflow-y-auto">{renderContent()}</div>
      </div>
    </div>
  );
};

export default CoinDetailModal;
